import React, { MouseEvent, MouseEventHandler, useCallback } from 'react';
import { generateTestId } from './utils/utils';
import { DefaultButton, IconButton, LinkButton, PrimarySecondaryButton, GenericButton } from './children';

export enum ButtonSize {
  MEDIUM = 'medium',
  LARGE = 'large',
}

export interface ButtonProps {
  /** Displaying text of the button */
  label: string;
  /** Style of the button */
  appearance?: ButtonAppearance;
  /** Optional onClick handler */
  onClick?: MouseEventHandler<HTMLElement>;
  /** Url for buttons being used as a link */
  href?: string;
  /** Specifies where the linked URL will be opened */
  target?: React.HTMLAttributeAnchorTarget;
  /** Icon to render before label */
  iconBefore?: React.ReactElement;
  /** Icon to render after label */
  iconAfter?: React.ReactElement;
  /** Type of icon */
  iconType?: string;
  /** Attribute type of the button */
  type?: 'button' | 'submit' | 'reset';
  /** Indicates if the button should fit container */
  fullWidth?: boolean;
  /** Button size */
  size?: ButtonSize;
  /** Indicates if the button is disabled. Default: false*/
  disabled?: boolean;
  /**Indicates if the button is selected.Default: true */
  selected?: boolean;
  /**Indicates if the button is in loading state */
  isLoading?: boolean;
  /**Indicates if button has extra padding */
  withPadding?: boolean;
  /** Tooltip for the button */
  tooltip?: string | null;
  /** Enables responsive styles for Primary and Secondary appearances */
  isResponsive?: boolean;
  /** Indicates the custom icon color */
  iconColor?: string;
  /** HTML element to render as ('button' | 'a'). Default: 'a' */
  as?: 'button' | 'a';
}

/**
 * @description Style of the button
 * @enum {string} DEFAULT | DANGER | LINK | PRIMARY | SUBTLE | SUBTLE_LINK | WARNING
 * @example ButtonAppearance.DEFAULT
 */
export enum ButtonAppearance {
  DEFAULT = 'default',
  DANGER = 'danger',
  LINK = 'link',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SUBTLE = 'subtle',
  WARNING = 'warning',
  ICON = 'icon',
  SUCCESS = 'success',
}

type ButtonComponentProps = ButtonProps & {
  clickHandler: (e: MouseEvent<HTMLElement>) => void;
  isButtonWithoutIcon: boolean;
  showBeforeIcon: React.ReactElement | false;
  showAfterIcon: React.ReactElement | false;
  isResponsiveSpacing: boolean;
  testId: string;
  as?: 'button' | 'a';
};

/**
 * @param {ButtonProps} ButtonProps
 * @returns {React.ReactElement} React.ReactElement
 *
 * @example
 * <Button label="CTA" />
 */
export const Button: React.FC<ButtonProps> = (props): React.ReactElement => {
  const {
    appearance = ButtonAppearance.DEFAULT,
    selected = false,
    disabled = false,
    size = ButtonSize.MEDIUM,
    onClick,
    iconBefore,
    iconAfter,
    isLoading = false,
    isResponsive = false,
  } = props;

  const clickHandler = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (onClick) {
        onClick(e);
      }
    },
    [onClick],
  );

  const isButtonWithoutIcon = !iconBefore && !iconAfter;
  const showBeforeIcon: React.ReactElement | false = (!isLoading && iconBefore) || false;
  const showAfterIcon: React.ReactElement | false = (!isLoading && iconAfter) || false;

  const isResponsiveSpacing =
    isResponsive &&
    [ButtonAppearance.PRIMARY, ButtonAppearance.SECONDARY].includes(appearance) &&
    size === ButtonSize.LARGE;

  const testId = generateTestId(appearance, selected, disabled);

  const commonProps = {
    ...props,
    clickHandler,
    isButtonWithoutIcon,
    showBeforeIcon,
    showAfterIcon,
    isResponsiveSpacing,
    testId,
  };

  const componentMap: Partial<Record<ButtonAppearance, React.ComponentType<ButtonComponentProps>>> = {
    [ButtonAppearance.DEFAULT]: DefaultButton,
    [ButtonAppearance.ICON]: IconButton,
    [ButtonAppearance.LINK]: LinkButton,
    [ButtonAppearance.PRIMARY]: PrimarySecondaryButton,
    [ButtonAppearance.SECONDARY]: PrimarySecondaryButton,
  };

  const Component = componentMap[appearance] ?? GenericButton;
  return <Component {...commonProps} />;
};
